import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiMenu, FiChevronDown, FiUser, FiLogOut, FiLock } from 'react-icons/fi';
import useAuthStore from '../../stores/useAuthStore';

const TopBar = ({ onMenuClick, isSidebarOpen, isMobile }) => {
  const navigate = useNavigate();
  const { user, logout, settings } = useAuthStore();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = () => {
    if (!user) return 'S';
    const firstName = user.firstname || user.firstName || '';
    const lastName = user.lastname || user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'S';
  };

  const getUserName = () => {
    if (!user) return 'Student';
    const first = user.firstname || user.firstName;
    const last = user.lastname || user.lastName;
    if (first) return `${first} ${last || ''}`.trim();
    return user.name || 'Student';
  };

  const getUserEmail = () => {
    if (!user) return '';
    return user.emailaddress || user.email || '';
  };

  return (
    <header className={`fixed top-0 right-0 h-16 bg-white border-b border-neutral-200 z-30 transition-all duration-300 ${isMobile ? 'left-0' : isSidebarOpen ? 'left-72' : 'left-20'}`}>
      <div className="flex items-center justify-between h-full px-4 sm:px-6">
        <div className="flex items-center space-x-4">
          <button onClick={onMenuClick} className="lg:hidden p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition-all">
            <FiMenu className="w-6 h-6" />
          </button>

          <div className="hidden sm:flex items-center space-x-3">
            <img src="/images/institution-branding.png" alt="ATBCOE Kangere" className="h-9 w-9 object-contain" />
            <div>
              <h1 className="text-sm font-semibold text-neutral-900 leading-none">{settings?.shortName || settings?.institutionName || 'ATBCOE Kangere'}</h1>
              <p className="text-xs text-neutral-500 leading-none mt-1">Student Portal</p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3 sm:space-x-4">
          <button onClick={() => navigate('/app/notifications')} className="relative p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition-all">
            <FiBell className="w-5 h-5" />
          </button>

          <div className="relative" ref={profileRef}>
            <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center space-x-2 px-2 sm:px-3 py-1.5 hover:bg-neutral-100 rounded-lg transition-all">
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {getInitials()}
              </div>
              <div className="hidden lg:block text-left">
                <div className="text-sm font-medium text-neutral-900 leading-none">{getUserName()}</div>
                <div className="text-xs text-neutral-500 leading-none mt-1 max-w-[200px] truncate">{getUserEmail() || 'Student'}</div>
              </div>
              <FiChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isProfileOpen && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-neutral-200 overflow-hidden">
                  <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-200">
                    <div className="text-sm font-semibold text-neutral-900">{getUserName()}</div>
                    <div className="text-xs text-neutral-500 mt-1">{getUserEmail() || 'Student'}</div>
                  </div>

                  <div className="py-2">
                    <button onClick={() => { navigate('/app/academic'); setIsProfileOpen(false); }} className="w-full flex items-center space-x-3 px-4 py-2.5 text-neutral-700 hover:bg-neutral-50 transition-colors">
                      <FiUser className="w-4 h-4" /><span className="text-sm font-medium">My Profile</span>
                    </button>
                    <button onClick={() => { navigate('/app/change'); setIsProfileOpen(false); }} className="w-full flex items-center space-x-3 px-4 py-2.5 text-neutral-700 hover:bg-neutral-50 transition-colors">
                      <FiLock className="w-4 h-4" /><span className="text-sm font-medium">Change Password</span>
                    </button>
                  </div>

                  <div className="border-t border-neutral-200">
                    <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-2.5 text-danger-600 hover:bg-danger-50 transition-colors">
                      <FiLogOut className="w-4 h-4" /><span className="text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
